<template>
  <div
    :class="[
      $style.container,
      { [$style['container--fromOther']]: !isSentByBiz },
    ]"
  >
    <div
      :class="[
        $style.container__chatMessageInfo,
        $style.chatMessageInfo,
        { [$style['chatMessageInfo--fromOther']]: !isSentByBiz },
      ]"
    >
      <p
        v-if="isSentByBiz && isReadByUser"
        :class="$style.chatMessageInfo__read"
      >
        既読
      </p>
      <p :class="$style.chatMessageInfo__time">
        <time>{{ timeString }}</time>
      </p>
    </div>
    <div
      :class="[
        $style.container__chatMessage,
        { [$style['container__chatMessage--fromOther']]: !isSentByBiz },
        $style.chatMessage,
      ]"
    >
      <UserThumbnail
        v-if="isSentByUser"
        :personality-type="personalityType"
        :class="$style.chatMessage__icon"
        @click="emit('clickUserThumbnail')"
      />
      <img
        v-if="isSentByBot"
        :src="BotImage"
        :class="$style.chatMessage__icon"
      />
      <div :class="$style.chatMessage__right">
        <p v-if="isSentByBot" :class="$style.chatMessage__name">
          自動メッセージ
        </p>
        <div
          :class="[
            $style.chatBalloon,
            {
              [$style['chatBalloon--fromOther']]: !isSentByBiz,
            },
          ]"
        >
          <slot />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { formatDateTimeForChatMessage } from '@/libs/utilities/formatDate'
import UserThumbnail from '@/components/atoms/UserThumbnail.vue'
import {
  type ChatMessageUserType,
  BizChatMessageUserType,
} from '../organisms/ChatMessageList/ChatMessageUserType'
import BotImage from '@/assets/images/chat-bot-image.png'
import type { PersonalityType } from '@/types/app'

const props = withDefaults(
  defineProps<{
    date: Date
    personalityType?: PersonalityType
    isReadByUser?: boolean
    userType: ChatMessageUserType
  }>(),
  {
    personalityType: 'A',
    isReadByUser: false,
  }
)

const emit = defineEmits<{
  (event: 'clickUserThumbnail'): void
}>()

const isSentByBiz = BizChatMessageUserType.includes(props.userType)
const isSentByUser = props.userType === 'user'
const isSentByBot = props.userType === 'bot'

const timeString = computed(() => formatDateTimeForChatMessage(props.date))
</script>

<style lang="scss" module>
.container {
  display: flex;
  align-items: flex-end;

  &--fromOther {
    flex-direction: row-reverse;
    padding-left: 0;
  }

  &__chatMessageInfo {
    flex: 0 1 120px;
  }

  &__chatMessage {
    flex: 1 1 auto;

    &:nth-child(n + 2) {
      margin-top: 8px;
      margin-right: 0;
      margin-left: 8px;
    }

    &--fromOther {
      &:nth-child(n + 2) {
        margin-right: 8px;
        margin-left: 0;
      }
    }
  }
}

.chatMessageInfo {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  color: $color-text-and-object-low-emphasis;
  font-size: 12px;

  &--fromOther {
    align-items: flex-start;
  }
}

.chatMessage {
  display: flex;

  &__icon {
    $size: 44px;

    width: $size;
    height: $size;
    margin-top: 4px;
  }

  &__right {
    flex: 1 1 0;

    &:nth-child(n + 2) {
      margin-left: 24px;
    }
  }

  &__name {
    color: $color-text-and-object-high-emphasis;
    font-size: 14px;
    font-weight: bold;
  }

  &__staffName {
    text-align: right;
    color: $color-text-and-object-low-emphasis;
    font-size: 12px;
  }
}

.chatBalloon {
  background-color: $color-surface-accent-primary;
  border-radius: $border-radius-v2-rounder;
  box-shadow: $shadow-24;
  color: $color-text-and-object-high-emphasis-inverse;
  font-size: 16px;
  font-weight: normal;
  line-height: 19px;

  &--fromOther {
    background-color: $color-surface-primary;
    color: $color-text-and-object-high-emphasis;
    font-weight: bold;
  }

  &:nth-child(n + 2) {
    margin-top: 4px;
  }
}
</style>
